import React from 'react';

import { Aggregation } from '@models/feature-descriptor';
import { TableColumns } from '@models/column';
import {
  DistanceEditor,
  TemporalChangeEditor,
  TemporalChangeCountEditor,
  AggregationTypeSelector,
  LastKEditor,
  TopicEditor,
  EditorWrapper,
  DraftEditorProps,
  TableColumnEditor,
  NumberEditor,
  StringEditor,
  TableEditor,
  BooleanEditor,
  ChipEditor,
} from './components';
import { AggregationDraft } from './models';
import {
  FeatureDescriptorEditorActions,
  useFeatureDescriptorEditorContext,
} from './context';

const AggregationEditorTemplate: Record<
  Partial<Aggregation.Type>,
  (props: DraftEditorProps<AggregationDraft>) => JSX.Element[]
> = {
  [Aggregation.Type.AbsoluteTemporalChangeCountRecords]: props =>
    TemporalChangeCountEditor(props),
  [Aggregation.Type.AbsoluteTemporalChangeMax]: props =>
    TemporalChangeEditor(props),
  [Aggregation.Type.AbsoluteTemporalChangeMean]: props =>
    TemporalChangeEditor(props),
  [Aggregation.Type.AbsoluteTemporalChangeMin]: props =>
    TemporalChangeEditor(props),
  [Aggregation.Type.AbsoluteTemporalChangeSum]: props =>
    TemporalChangeEditor(props),

  [Aggregation.Type.AnyBetween]: props => [
    <TableColumnEditor label="field" {...props} />,
    <NumberEditor label="lower_edge" {...props} />,
    <NumberEditor label="upper_edge" {...props} />,
  ],
  [Aggregation.Type.AnyValue]: props => [
    <TableColumnEditor label="field" {...props} />,
    <NumberEditor label="value" {...props} />,
  ],
  [Aggregation.Type.AsIs]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.C3]: props => [
    <TableColumnEditor label="source_time_field" {...props} />,
    <TableColumnEditor label="source_field" {...props} />,
    <NumberEditor label="lag" {...props} />,
  ],
  [Aggregation.Type.CWT]: props => [
    <TableColumnEditor label="source_time_field" {...props} />,
    <TableColumnEditor label="source_field" {...props} />,
    <NumberEditor label="index" {...props} />,
    <NumberEditor label="width" {...props} />,
  ],
  [Aggregation.Type.ClosestTimeDifference]: props => [
    <TableColumnEditor label="source_field" {...props} />,
    <TableColumnEditor label="target_field" {...props} />,
    <StringEditor label="shift" {...props} />,
  ],
  [Aggregation.Type.Count]: props => [
    <TableEditor
      label="field"
      {...props}
      tables={Object.keys(props.tableColumns)}
    />,
  ],
  [Aggregation.Type.CountBetween]: props => [
    <TableColumnEditor label="field" {...props} />,
    <NumberEditor label="lower_edge" {...props} />,
    <NumberEditor label="upper_edge" {...props} />,
  ],
  [Aggregation.Type.CountOfValue]: props => [
    <TableColumnEditor label="field" {...props} />,
    <NumberEditor label="value" {...props} />,
  ],
  [Aggregation.Type.CountRecords]: props => [
    <TableEditor
      label="table"
      tables={Object.keys(props.tableColumns)}
      {...props}
    />,
  ],
  [Aggregation.Type.CountUnique]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.DayOfWeek]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.Distance]: props => DistanceEditor(props),
  [Aggregation.Type.Exist]: props => [
    <TableEditor
      label="table"
      tables={Object.keys(props.tableColumns)}
      {...props}
    />,
  ],
  [Aggregation.Type.GridTargetMeanEncode]: props => [
    <TableColumnEditor label="latitude_field" {...props} />,
    <TableColumnEditor label="longitude_field" {...props} />,
    <NumberEditor label="encoder_id" {...props} />,
    <StringEditor label="distance" {...props} />,
  ],
  [Aggregation.Type.HistogramEqualityRatio]: props => [
    <TableColumnEditor label="field" {...props} />,
    <NumberEditor label="value" {...props} />,
  ],
  [Aggregation.Type.Hour]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.LatestKMax]: props => LastKEditor(props),
  [Aggregation.Type.LatestKMaxMinusMin]: props => LastKEditor(props),
  [Aggregation.Type.LatestKMean]: props => LastKEditor(props),
  [Aggregation.Type.LatestKMin]: props => LastKEditor(props),
  [Aggregation.Type.LatestKStd]: props => LastKEditor(props),
  [Aggregation.Type.LatestKSum]: props => LastKEditor(props),
  [Aggregation.Type.LatestTimeDifference]: props => [
    <TableColumnEditor label="target_field" {...props} />,
    <TableColumnEditor label="source_field" {...props} />,
    <StringEditor label="shift" {...props} />,
  ],
  [Aggregation.Type.Max]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.MaxDistance]: props => DistanceEditor(props),
  [Aggregation.Type.MaxInterval]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.Mean]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.MeanDistance]: props => DistanceEditor(props),
  [Aggregation.Type.MeanInterval]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.MeanTimeDifference]: props => [
    <TableColumnEditor label="column1" {...props} />,
    <TableColumnEditor label="column2" {...props} />,
    <StringEditor label="unit" {...props} />,
  ],
  [Aggregation.Type.Min]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.MinDistance]: props => DistanceEditor(props),
  [Aggregation.Type.MinInterval]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.Month]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.N1RedundantMean]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.N1RedundantNullRatio]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.N1RedundantRatioOfValue]: props => [
    <TableColumnEditor label="field" {...props} />,
    <StringEditor label="ratio_value" {...props} />,
    <BooleanEditor label="null_inclusion" {...props} />,
  ],
  [Aggregation.Type.N1RedundantSum]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.N1RedundantTopicWeightMean]: props => [
    <TableColumnEditor label="text_topic_field" {...props} />,
    <TableColumnEditor label="original_text_column_name" {...props} />,
    <NumberEditor label="ratio_value" {...props} />,
  ],
  [Aggregation.Type.N1RedundantTopicWeightSum]: props => [
    <TableColumnEditor label="text_topic_field" {...props} />,
    <TableColumnEditor label="original_text_column_name" {...props} />,
    <NumberEditor label="ratio_value" {...props} />,
  ],

  [Aggregation.Type.NgramCount]: props => [
    <TableColumnEditor label="field" {...props} />,
    <ChipEditor label="ngram" {...props} />,
  ],
  [Aggregation.Type.NonBusinessDays]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.NullRatio]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.RatioBetween]: props => [
    <TableColumnEditor label="field" {...props} />,
    <NumberEditor label="lower_edge" {...props} />,
    <NumberEditor label="upper_edge" {...props} />,
  ],
  [Aggregation.Type.RatioOfValue]: props => [
    <TableColumnEditor label="field" {...props} />,
    <StringEditor label="ratio_value" {...props} />,
    <BooleanEditor label="null_inclusion" {...props} />,
  ],
  [Aggregation.Type.Std]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.StdDistance]: props => DistanceEditor(props),
  [Aggregation.Type.StdInterval]: props => [
    <TableColumnEditor label="field" {...props} />,
  ],
  [Aggregation.Type.SumDistance]: props => DistanceEditor(props),

  [Aggregation.Type.StdTimeDifference]: props => [
    <TableColumnEditor label="column1" {...props} />,
    <TableColumnEditor label="column2" {...props} />,
    <StringEditor label="unit" {...props} />,
  ],
  [Aggregation.Type.Sum]: props => [
    <TableColumnEditor label="column" {...props} />,
  ],
  [Aggregation.Type.SumDistance]: props => DistanceEditor(props),

  [Aggregation.Type.TargetMeanEncode]: props => [
    <TableColumnEditor label="field" {...props} />,
    <NumberEditor label="encoder_id" {...props} />,
  ],
  [Aggregation.Type.TargetTimeDifference]: props => [
    <TableColumnEditor label="source_field" {...props} />,
    <TableColumnEditor label="target_field" {...props} />,
    <StringEditor label="shift" {...props} />,
  ],

  [Aggregation.Type.TemporalChangeCountRecords]: props =>
    TemporalChangeCountEditor(props),
  [Aggregation.Type.TemporalChangeMax]: props => TemporalChangeEditor(props),
  [Aggregation.Type.TemporalChangeMean]: props => TemporalChangeEditor(props),
  [Aggregation.Type.TemporalChangeMin]: props => TemporalChangeEditor(props),
  [Aggregation.Type.TemporalChangeSum]: props => TemporalChangeEditor(props),

  [Aggregation.Type.TemporalChangeRatioCountRecords]: props =>
    TemporalChangeCountEditor(props),
  [Aggregation.Type.TemporalChangeRatioMax]: props =>
    TemporalChangeEditor(props),
  [Aggregation.Type.TemporalChangeRatioMean]: props =>
    TemporalChangeEditor(props),
  [Aggregation.Type.TemporalChangeRatioMin]: props =>
    TemporalChangeEditor(props),
  [Aggregation.Type.TemporalChangeRatioSum]: props =>
    TemporalChangeEditor(props),

  [Aggregation.Type.TimeDifference]: props => [
    <TableColumnEditor label="column1" {...props} />,
    <TableColumnEditor label="column2" {...props} />,
    <StringEditor label="unit" {...props} />,
  ],
  [Aggregation.Type.TopicWeightMax]: props => TopicEditor(props),
  [Aggregation.Type.TopicWeightMean]: props => TopicEditor(props),
  [Aggregation.Type.TopicWeightMin]: props => TopicEditor(props),
  [Aggregation.Type.TopicWeightSum]: props => TopicEditor(props),
  [Aggregation.Type.TopicWeightValue]: props => TopicEditor(props),
};

interface InlineAggregationEditorEditor {
  tableColumns: TableColumns;
  aggregation: AggregationDraft;
  onAggregationChange: (aggregation: AggregationDraft) => void;
}

const InlineAggregationEditor = (props: InlineAggregationEditorEditor) => {
  const templates = props.aggregation.type
    ? AggregationEditorTemplate[props.aggregation.type as Aggregation.Type]?.({
        tableColumns: props.tableColumns,
        draft: props.aggregation,
        onDraftChange: props.onAggregationChange,
      })
    : [];

  return (
    <EditorWrapper
      templates={templates}
      selectedType={props.aggregation.type}
      selectorWidth={300}
    >
      <AggregationTypeSelector
        selectedType={props.aggregation.type}
        onTypeChange={type => {
          props.onAggregationChange({ type });
        }}
      />
    </EditorWrapper>
  );
};

export interface AggregationEditorProps {
  tableColumns: TableColumns;
}

export const AggregationEditor = ({ tableColumns }: AggregationEditorProps) => {
  const [state, dispatch] = useFeatureDescriptorEditorContext();
  const aggregationDraft = state.aggregation as AggregationDraft;

  return (
    <InlineAggregationEditor
      aggregation={aggregationDraft}
      onAggregationChange={value => {
        dispatch(
          FeatureDescriptorEditorActions.setDraftField({
            field: 'aggregation',
            value,
          })
        );
      }}
      tableColumns={tableColumns}
    />
  );
};
